import React, { useState, useEffect } from "react";
import "./Track.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FaBox, FaTruck, FaCheckCircle, FaClipboardList } from "react-icons/fa";

const steps = [
  { label: "Payment Received", icon: <FaClipboardList /> },
  { label: "Order Packed", icon: <FaBox /> },
  { label: "Order Shipped", icon: <FaTruck /> },
  { label: "Order Received", icon: <FaCheckCircle /> },
];

const Tracking = () => {
  // State for eight-digit inputs (desktop/tablet)
  const [trackingDigits, setTrackingDigits] = useState(Array(8).fill(""));
  // State for mobile single text input
  const [mobileTrackingCode, setMobileTrackingCode] = useState("");
  // State to check if the screen is mobile sized
  const [isMobile, setIsMobile] = useState(window.innerWidth < 480);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);

  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 480);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // For eight individual digit inputs (non-mobile)
  const handleInputChange = (e, index) => {
    const value = e.target.value;
    // Allow only a single digit (0-9) or empty
    if (value === "" || /^[0-9]$/.test(value)) {
      const newDigits = [...trackingDigits];
      newDigits[index] = value;
      setTrackingDigits(newDigits);
      // If user typed a digit, automatically focus the next input (if any)
      if (value !== "" && index < 7) {
        document.getElementById(`digit-${index + 1}`)?.focus();
      }
    }
  };

  // Handle key events for the eight-digit inputs
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (trackingDigits[index]) {
        e.preventDefault();
        const newDigits = [...trackingDigits];
        newDigits[index] = "";
        setTrackingDigits(newDigits);
      } else {
        if (index > 0) {
          e.preventDefault();
          const newDigits = [...trackingDigits];
          newDigits[index - 1] = "";
          setTrackingDigits(newDigits);
          document.getElementById(`digit-${index - 1}`)?.focus();
        }
      }
    } else if (e.key === "Enter") {
      handleTrackOrder();
    }
  };

  // Handle mobile single text input change
  const handleMobileInputChange = (e) => {
    const value = e.target.value;
    // Allow only digits
    if (value === "" || /^[0-9]*$/.test(value)) {
      setMobileTrackingCode(value);
    }
  };

  // Track order function using the appropriate tracking code input
  const handleTrackOrder = async () => {
    const trackingCode = isMobile ? mobileTrackingCode : trackingDigits.join("");

    // Validate tracking code length (should be exactly 8 digits)
    if (trackingCode.length !== 8) {
      setError("Tracking number not found");
      setProgress(null);
      setOrderDetails(null);
      return;
    }

    setError("");

    const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
    const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/orders?customer_track_code=eq.${trackingCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "apikey": SUPABASE_KEY,
            "Authorization": `Bearer ${SUPABASE_KEY}`,
          },
        }
      );
      const data = await response.json();

      if (data.length === 0) {
        setError("Tracking number not found");
        setProgress(null);
        setOrderDetails(null);
      } else {
        const order = data[0];
        let progressIndex = -1;
        if (order.payment) progressIndex = 0;
        if (order.packed) progressIndex = 1;
        if (order.shipped) progressIndex = 2;
        if (order.recieved) progressIndex = 3;
        setProgress(progressIndex);
        setOrderDetails(order);
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setError("Error fetching order");
      setProgress(null);
      setOrderDetails(null);
    }
  };

  return (
    <div className="tracking-page">
      <Header />

      <div className="tracking-container">
        <h2>Track Your Order</h2>

        {isMobile ? (
          // Single text input for mobile view
          <div className="tracking-input">
            <input
              type="text"
              value={mobileTrackingCode}
              onChange={handleMobileInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleTrackOrder();
              }}
              maxLength="8"
              className="tracking-text-input"
              placeholder="Enter tracking number"
            />
            <button onClick={handleTrackOrder} className="tracking-button">
              Track
            </button>
          </div>
        ) : (
          // Eight separate digit inputs for larger screens
          <div className="tracking-input">
            <div className="tracking-digits-container">
              {trackingDigits.map((digit, index) => (
                <input
                  key={index}
                  id={`digit-${index}`}
                  type="text"
                  value={digit}
                  onChange={(e) => handleInputChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  maxLength="1"
                  className="tracking-digit-input"
                />
              ))}
            </div>
            <button onClick={handleTrackOrder} className="tracking-button">
              Track
            </button>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {orderDetails && progress !== null && !error && (
          <div className="tracking-status">
            <h3>Order Status</h3>
            <div className="progress-container">
              <div className="progress-line">
                <div
                  className="progress-fill"
                  style={{ width: `${(progress / (steps.length - 1)) * 100}%` }}
                ></div>
              </div>
              <div className="progress-steps">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className={`progress-step ${index <= progress ? "active" : ""}`}
                  >
                    <div className="step-icon">{step.icon}</div>
                    <p>{step.label}</p>
                  </div>
                ))}
              </div>
            </div>
            {orderDetails.usps_tracking_number && (
              <p>
                Your USPS tracking number:{" "}
                <a
                  href={`https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${orderDetails.usps_tracking_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>{orderDetails.usps_tracking_number}</strong>
                </a>
              </p>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Tracking;
